/**
 * Entidades e resultado da validação — espelham as tabelas `valida_*` do TMobi,
 * para que o mesmo servidor e o mesmo protocolo de sync continuem valendo.
 */

/** `valida_valida_lote` */
export interface Lote {
  id?: number;
  eventoId: number;
  /** Faixa de códigos do lote; o casamento é por LIKE com os dígitos 2-5 do ingresso. */
  loteStr: string;
  setor: string;
  setorId: number;
  /** Valor em consumação — string para não passar por float (regra da casa: dinheiro nunca é float). */
  consumacao: string;
}

/** `valida_setor` — `habilitado` é o filtro que o operador liga/desliga na tela de setores. */
export interface Setor {
  id?: number;
  remoteId: number;
  eventoId: number;
  nome: string;
  habilitado: boolean;
}

/** `valida_ingresso_online` — ingresso nominal, com os dados do portador. */
export interface IngressoOnline {
  id?: number;
  eventoId: number;
  codbar: string;
  nome: string;
  email: string;
  cpf: string;
  pedidoId: string;
  status: string;
  setor: string;
  setorId: number;
  numero: string;
}

/** `valida_ingresso_cancelado` */
export interface IngressoCancelado {
  eventoId: number;
  codbar: string;
  dataCancelamento: string;
  motivoCancelamento: string;
  usuario: string;
}

/** `valida_validado` — a validação feita neste aparelho, que sobe no próximo sync. */
export interface Validado {
  id?: number;
  eventoId: number;
  codbar: string;
  /** ISO 8601 local. */
  dataHora: string;
  setorId: string;
  consumacao: string;
  /** false enquanto o servidor não confirmou o recebimento. */
  enviado: boolean;
}

export type MotivoValidacao =
  | 'VALIDADO'
  | 'CODIGO_INVALIDO'
  | 'PASSAPORTE_FORA_DA_DATA'
  | 'CANCELADO'
  | 'JA_VALIDADO'
  | 'LOTE_INVALIDO'
  | 'SETOR_INVALIDO'
  | 'SETOR_NAO_HABILITADO'
  | 'OUTRO_EVENTO'
  | 'STATUS_CANCELADO'
  | 'STATUS_PENDENTE'
  | 'AGUARDANDO_CONFIRMACAO_DADOS';

/**
 * Status do ingresso nominal, como o servidor manda (`CValidaSync.getStatusNome`
 * no TMobi original): `A` aprovado, `C` cancelado/devolvido, `W` aguardando —
 * qualquer outra letra é tratada como pendente.
 *
 * Só `A` e `W` entram na festa, que é a mesma regra da busca ao portador do app
 * original (`CValidaPesquisaDialog`).
 */
export const STATUS_QUE_ENTRAM = ['A', 'W'];

/**
 * Nome do ícone de cada desfecho. O desenho mora em `src/ui/icones.tsx` — aqui
 * fica só a escolha, porque ela é parte do contrato com o operador tanto quanto
 * a cor: quem não distingue verde de vermelho lê o ícone.
 */
export type IconeDesfecho =
  | 'entrou'
  | 'relogio'
  | 'proibido'
  | 'alertaTriangulo'
  | 'alertaCirculo'
  | 'ingresso'
  | 'lote'
  | 'setor'
  | 'setorFora'
  | 'outroEvento'
  | 'calendarioFora';

export interface Desfecho {
  mensagem: string;
  cor: string;
  /**
   * Tinta do texto e do ícone SOBRE `cor`. Cadastrada e não derivada: a maioria
   * é branca, mas o verde do "confirmar dados" só aceita tinta escura, e um
   * cálculo de luminância erra justamente nos tons médios.
   */
  tinta: string;
  /** A palavra que se lê a três metros, antes de ler a mensagem. */
  palavra: string;
  icone: IconeDesfecho;
}

/**
 * Mensagem, cor, palavra e ícone de cada desfecho. É contrato com o operador de
 * portaria: ele reconhece a tela antes de ler o texto. As mensagens são as
 * mesmas do validador antigo, sem uma vírgula trocada; os hues são os do TMobi
 * escurecidos o bastante para o branco continuar legível sob sol, e o ícone é a
 * rede de segurança de quando a cor falha.
 *
 * Isto não é decisão de design e não muda sem pedido explícito.
 */
export const DESFECHO: Record<MotivoValidacao, Desfecho> = {
  VALIDADO: {
    mensagem: 'Ingresso validado com sucesso',
    cor: '#00A63E',
    tinta: '#FFFFFF',
    palavra: 'VALIDADO',
    icone: 'entrou',
  },
  CODIGO_INVALIDO: {
    mensagem: 'Ingresso invalido',
    cor: '#D97706',
    tinta: '#FFFFFF',
    palavra: 'INVÁLIDO',
    icone: 'alertaTriangulo',
  },
  PASSAPORTE_FORA_DA_DATA: {
    mensagem: 'Passaporte não pertence ao dia',
    // O âmbar é o do CValidaResult do original, sem escurecer. Por isso a
    // tinta é escura: neste tom o branco não se lê sob sol, e é o mesmo caso
    // do CONFIRA — a razão de a tinta ser cadastrada e não calculada.
    cor: '#FF8F00',
    tinta: '#2A1800',
    palavra: 'OUTRO DIA',
    icone: 'calendarioFora',
  },
  CANCELADO: {
    mensagem: 'Ingresso cancelado',
    cor: '#DC2626',
    tinta: '#FFFFFF',
    palavra: 'CANCELADO',
    icone: 'proibido',
  },
  JA_VALIDADO: {
    mensagem: 'Ingresso já validado',
    cor: '#0284C7',
    tinta: '#FFFFFF',
    palavra: 'JÁ VALIDADO',
    icone: 'relogio',
  },
  LOTE_INVALIDO: {
    mensagem: 'Inválido lote',
    cor: '#C026D3',
    tinta: '#FFFFFF',
    palavra: 'LOTE',
    icone: 'lote',
  },
  SETOR_INVALIDO: {
    mensagem: 'Inválido setor',
    cor: '#52616F',
    tinta: '#FFFFFF',
    palavra: 'SETOR',
    icone: 'setor',
  },
  SETOR_NAO_HABILITADO: {
    mensagem: 'Não pertence ao setor',
    cor: '#25303B',
    tinta: '#FFFFFF',
    palavra: 'OUTRO SETOR',
    icone: 'setorFora',
  },
  OUTRO_EVENTO: {
    mensagem: 'Ingresso inválido',
    cor: '#E85D28',
    tinta: '#FFFFFF',
    palavra: 'OUTRO EVENTO',
    icone: 'outroEvento',
  },
  STATUS_CANCELADO: {
    mensagem: 'Ingresso cancelado/devolvido',
    cor: '#D14545',
    tinta: '#FFFFFF',
    palavra: 'DEVOLVIDO',
    icone: 'proibido',
  },
  STATUS_PENDENTE: {
    mensagem: 'Ingresso pendente',
    cor: '#C2680B',
    tinta: '#FFFFFF',
    palavra: 'PENDENTE',
    icone: 'alertaCirculo',
  },
  AGUARDANDO_CONFIRMACAO_DADOS: {
    mensagem: 'Confirmar dados do portador',
    cor: '#4AFD22',
    tinta: '#2A1800',
    palavra: 'CONFIRA',
    icone: 'ingresso',
  },
};

export interface ResultadoValidacao {
  /** true só quando a validação foi gravada. */
  valido: boolean;
  motivo: MotivoValidacao;
  mensagem: string;
  cor: string;
  /** Tinta sobre a cor do desfecho — ver DESFECHO. */
  tinta: string;
  /** A palavra grande da tela de resultado. */
  palavra: string;
  icone: IconeDesfecho;
  codbar: string;
  /** true = a tela de resultado espera toque do operador em vez de sumir sozinha. */
  exigeConfirmacao: boolean;
  /** Milissegundos que a tela de resultado fica visível quando some sozinha. */
  timeoutMs: number;
  consumacao?: string;
  online?: IngressoOnline;
  cancelado?: IngressoCancelado;
  /** Preenchido em JA_VALIDADO: quando e onde já tinha sido validado. */
  validadoEm?: Validado;
  /**
   * Lote e setor resolvidos pela cadeia, a partir do passo em que cada um é
   * encontrado. Vêm do código de barras, não do cadastro online — é o que
   * permite mostrar setor na tela de um ingresso não-nominal, que não tem
   * registro em `ingresso_online`.
   */
  lote?: Lote;
  setor?: Setor;
}

/** Evento disponível para validação, baixado no login. */
export interface Evento {
  id: number;
  nome: string;
  data?: string;
  local?: string;
  cidade?: string;
  /**
   * URL do banner do evento, para o operador reconhecer o cartão pela arte
   * antes de ler o nome. Vem do `bannerMd5` do servidor — o nome é herdado, o
   * conteúdo é o endereço inteiro da imagem.
   *
   * É enfeite: mora no site da ticketeira, então sem rede não carrega, e a
   * escolha do evento não pode depender dela.
   */
  banner?: string;
}

/** Operador autenticado na ticketeira. */
export interface Sessao {
  usuarioId: string;
  nome?: string;
  /** `device_id` devolvido pelo servidor; vira o `machineId` do sync. */
  machineId: number;
  ticketeira: string;
  autenticadoEm: string;
}
